import { PromptPurposeBadge } from '@/components/prompts/PromptPurposeBadge';
import type { PromptPurpose } from '@/components/prompts/prompt-purpose-utils';
import { Badge, type CrudConfig } from '@startupquickstart/frontend';

/** Shape of a Prompt record as returned by the API list endpoint */
export interface Prompt {
  id: string;
  name: string;
  purpose: PromptPurpose;
  description?: string | null;
  isDefault: boolean;
  activeVersionId?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Included in list responses for lightweight display */
  activeVersion?: {
    id: string;
    versionNumber: number;
  } | null;
}

/** Full prompt shape returned by the single-record endpoint */
export interface PromptWithVersion extends Prompt {
  activeVersion?: {
    id: string;
    versionNumber: number;
    body: string;
    changelog?: string | null;
    createdAt: string;
  } | null;
  rules?: Array<{
    id: string;
    body: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }>;
}

/**
 * Prompt CRUD configuration for use with CrudListView and related components.
 */
export const promptCrudConfig: CrudConfig<Prompt> = {
  entityName: 'Prompt',
  entityNamePlural: 'Prompts',

  endpoints: {
    list: '/prompts',
    single: (id: string) => `/prompts/${id}`,
    create: '/prompts',
    update: (id: string) => `/prompts/${id}`,
    delete: (id: string) => `/prompts/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter prompt name',
      validation: {
        min: 2,
        max: 255,
      },
    },
    {
      key: 'purpose',
      label: 'Purpose',
      type: 'select',
      required: true,
      options: [
        { label: 'Attraction Short Description', value: 'attraction_short_description' },
        { label: 'Attraction Long Description', value: 'attraction_long_description' },
        { label: 'Tour Content', value: 'tour_content' },
        { label: 'Tour Conclusions', value: 'tour_conclusions' },
        { label: 'Themed Short Description', value: 'themed_short_description' },
        { label: 'Themed Long Description', value: 'themed_long_description' },
        { label: 'Attraction Metadata', value: 'attraction_metadata' },
        { label: 'Attraction List', value: 'attraction_list' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <PromptPurposeBadge purpose={value as PromptPurpose} />;
      },
    },
    {
      key: 'isDefault',
      label: 'Default',
      type: 'boolean',
      required: false,
      format: (value: boolean) => {
        if (!value) return null;
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
            Default
          </Badge>
        );
      },
    },
    {
      key: 'activeVersion',
      label: 'Active Version',
      type: 'custom',
      required: false,
      format: (value: Prompt['activeVersion']) => {
        if (!value) return <span className="text-muted-foreground">—</span>;
        return (
          <Badge variant="secondary">v{value.versionNumber}</Badge>
        );
      },
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['name', 'purpose', 'isDefault', 'activeVersion', 'updatedAt'],
    searchableFields: ['name'],
    sortableFields: ['name', 'purpose', 'isDefault', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 20,
    filters: [
      {
        key: 'purpose',
        label: 'Purpose',
        type: 'multiselect',
        options: [
          { label: 'Attraction Short Description', value: 'attraction_short_description' },
        { label: 'Attraction Long Description', value: 'attraction_long_description' },
          { label: 'Tour Content', value: 'tour_content' },
          { label: 'Tour Conclusions', value: 'tour_conclusions' },
          { label: 'Themed Short Description', value: 'themed_short_description' },
        { label: 'Themed Long Description', value: 'themed_long_description' },
          { label: 'Attraction Metadata', value: 'attraction_metadata' },
          { label: 'Attraction List', value: 'attraction_list' },
        ],
      },
    ],
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'General',
        description: 'Core prompt details',
        fields: ['name', 'purpose', 'isDefault', 'activeVersion'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'Core prompt details',
        fields: ['name', 'purpose'],
        columns: 1,
      },
    ],
    validation: {
      name: { required: true, min: 2, max: 255 },
      purpose: { required: true },
    },
  },
};
